/**
 * Nosto-CompleteLookItem.js
 *
 * Controla un producto individual dentro del drawer:
 * - selección de talla;
 * - actualización de variante;
 * - cambio de producto por color;
 * - navegación directa al checkout.
 */
(function initializeNostoCompleteLookItem(global) {
    "use strict";

    class NostoCompleteLookItem extends HTMLElement {
        constructor() {
            super();

            this.requestController = null;
            this.isSwitchingColor = false;

            this.handleClick =
                this.handleClick.bind(this);

            this.handleProductAdd =
                this.handleProductAdd.bind(this);
        }

        connectedCallback() {
            this.addEventListener(
                "click",
                this.handleClick
            );

            document.addEventListener(
                "theme:product:add",
                this.handleProductAdd
            );
        }

        disconnectedCallback() {
            this.removeEventListener(
                "click",
                this.handleClick
            );

            document.removeEventListener(
                "theme:product:add",
                this.handleProductAdd
            );

            this.requestController?.abort();
        }

        normalizeString(value) {
            return String(value ?? "").trim();
        }

        get viewService() {
            return (
                global.nostoProductViewService ||
                global.NOSTO_PRODUCT_VIEW_SERVICE ||
                global.NOSTO_PLP?.productViewService ||
                global.NOSTO_SEARCH?.productViewService ||
                null
            );
        }

        handleClick(event) {
            const sizeButton =
                event.target.closest(
                    "[data-complete-look-size]"
                );

            if (sizeButton) {
                event.preventDefault();
                this.selectSize(sizeButton);
                return;
            }

            const colorButton =
                event.target.closest(
                    "[data-complete-look-color]"
                );

            if (colorButton) {
                event.preventDefault();
                this.selectColor(colorButton);
                return;
            }
        }

        handleProductAdd(event) {
            const submittedButton =
                event.detail?.button;

            if (
                !(submittedButton instanceof Element) ||
                !this.contains(submittedButton) ||
                !submittedButton.matches(
                    "[data-complete-look-add-to-cart]"
                )
            ) {
                return;
            }

            this.closeDrawer();
        }

        selectSize(button) {
            if (
                button.disabled ||
                button.dataset.available !== "true"
            ) {
                return;
            }

            const variantId =
                this.normalizeString(
                    button.dataset.variantId
                );

            if (!variantId) {
                return;
            }

            this.querySelectorAll(
                "[data-complete-look-size]"
            ).forEach((sizeButton) => {
                const isActive =
                    sizeButton === button;

                sizeButton.classList.toggle(
                    "is-active",
                    isActive
                );

                sizeButton.setAttribute(
                    "aria-pressed",
                    String(isActive)
                );
            });

            const variantInput =
                this.querySelector(
                    "[data-complete-look-variant-id]"
                );

            if (variantInput) {
                variantInput.value =
                    variantId;
            }

            this.dataset.selectedVariantId =
                variantId;

            this.updatePrice(button);

            const addButton =
                this.querySelector(
                    "[data-complete-look-add-to-cart]"
                );

            if (addButton) {
                addButton.disabled = false;

                const addButtonText =
                    addButton.querySelector(
                        "[data-add-to-cart-text]"
                    );

                if (addButtonText) {
                    addButtonText.textContent =
                        "Agregar a la bolsa";
                }
            }
        }

        updatePrice(sizeButton) {
            const price =
                sizeButton.dataset.price || "";

            const comparePrice =
                sizeButton.dataset.comparePrice || "";

            const priceElement =
                this.querySelector(
                    "[data-complete-look-item-price]"
                );

            const compareElement =
                this.querySelector(
                    "[data-complete-look-item-compare-price]"
                );

            if (priceElement && price) {
                priceElement.textContent =
                    price;

                priceElement.classList.toggle(
                    "is-sale",
                    Boolean(comparePrice)
                );
            }

            if (compareElement) {
                compareElement.hidden =
                    !comparePrice;

                compareElement.textContent =
                    comparePrice;
            }
        }

        async selectColor(button) {
            if (
                button.disabled ||
                this.isSwitchingColor
            ) {
                return;
            }

            const productId =
                this.normalizeString(
                    button.dataset.productId
                );

            const handle =
                this.normalizeString(
                    button.dataset.productHandle
                );

            if (!handle) {
                return;
            }

            const viewService =
                this.viewService;

            if (
                !viewService ||
                typeof viewService.getView !==
                "function"
            ) {
                console.error(
                    "[NostoCompleteLookItem] NostoProductViewService no está disponible."
                );

                return;
            }

            this.requestController?.abort();

            this.requestController =
                new AbortController();

            this.isSwitchingColor = true;
            this.classList.add(
                "is-switching-color"
            );

            this.querySelectorAll(
                "[data-complete-look-color]"
            ).forEach((colorButton) => {
                colorButton.disabled = true;
            });

            button.classList.add(
                "is-loading"
            );

            try {
                const html =
                    await viewService.getView({
                        handle,
                        productId,
                        view:
                            "nosto-complete-look-item",
                        signal:
                            this.requestController.signal,
                        validationSelector:
                            "[data-nosto-complete-look-item]",
                        context: {
                            source:
                                "complete-look-drawer-color",
                            previousProductId:
                                this.dataset.productId || "",
                        },
                    });

                const template =
                    document.createElement(
                        "template"
                    );

                template.innerHTML =
                    String(html).trim();

                const nextItem =
                    template.content.querySelector(
                        "[data-nosto-complete-look-item]"
                    );

                if (!nextItem) {
                    throw new Error(
                        "La vista no contiene [data-nosto-complete-look-item]."
                    );
                }

                /*
                 * Al insertar el nuevo custom element,
                 * connectedCallback vuelve a inicializarlo.
                 */
                this.replaceWith(
                    document.importNode(
                        nextItem,
                        true
                    )
                );
            } catch (error) {
                if (
                    error?.name !== "AbortError"
                ) {
                    console.error(
                        "[NostoCompleteLookItem] No fue posible cambiar el color.",
                        error
                    );

                    this.querySelectorAll(
                        "[data-complete-look-color]"
                    ).forEach((colorButton) => {
                        colorButton.disabled =
                            colorButton.classList.contains(
                                "is-active"
                            );
                    });
                }
            } finally {
                this.isSwitchingColor = false;
                this.requestController = null;

                this.classList.remove(
                    "is-switching-color"
                );
            }
        }
    }

    if (
        !customElements.get(
            "nosto-complete-look-item"
        )
    ) {
        customElements.define(
            "nosto-complete-look-item",
            NostoCompleteLookItem
        );
    }
})(window);